import {
  maakAfbeeldingItem,
  maakAudioItem,
  maakVideoItem,
  secondeNaarFrame,
  tracksVanType,
  voegItemToe,
  volgendVrijFrame,
  type ProjectModel,
  type TrackType,
} from '@video-studio/shared';
import type { MediaDto } from '../api/client';

/** Eigen sleepsoort per spoor, zodat de tijdlijn tijdens het slepen het juiste spoor kan oplichten. */
export const SLEEP_VIDEO = 'application/x-videostudio-video';
export const SLEEP_AFBEELDING = 'application/x-videostudio-afbeelding';
export const SLEEP_AUDIO = 'application/x-videostudio-audio';

const SLEEP_PER_SPOOR: Record<'video' | 'afbeelding' | 'audio', string> = {
  video: SLEEP_VIDEO,
  afbeelding: SLEEP_AFBEELDING,
  audio: SLEEP_AUDIO,
};

export interface PlaatsEditor {
  project: ProjectModel | null;
  bewerk: (fn: (project: ProjectModel) => ProjectModel, beschrijving: string) => void;
  selecteer: (itemId: string | null) => void;
}

export interface PlaatsPlan {
  itemId: string;
  startFrame: number;
  duurInFrames: number;
  trackType: 'video' | 'afbeelding' | 'audio';
  beschrijving: string;
}

export function trackTypeVoorMedia(item: Pick<MediaDto, 'soort' | 'mimeType'>): 'video' | 'afbeelding' | 'audio' | null {
  if (item.soort === 'video') return 'video';
  if (item.soort === 'audio') return 'audio';
  if (item.soort === 'afbeelding' || item.mimeType.startsWith('image/')) return 'afbeelding';
  return null;
}

export function sleepTypeVoorMedia(item: Pick<MediaDto, 'soort' | 'mimeType'>): string | null {
  const spoor = trackTypeVoorMedia(item);
  return spoor ? SLEEP_PER_SPOOR[spoor] : null;
}

export function spoorUitSleepTypes(types: Iterable<string>): 'video' | 'afbeelding' | 'audio' | null {
  const lijst = new Set(types);
  if (lijst.has(SLEEP_VIDEO)) return 'video';
  if (lijst.has(SLEEP_AFBEELDING)) return 'afbeelding';
  if (lijst.has(SLEEP_AUDIO)) return 'audio';
  return null;
}

export function leesGesleeptId(data: DataTransfer): string {
  for (const type of Object.values(SLEEP_PER_SPOOR)) {
    const id = data.getData(type);
    if (id) return id;
  }
  return '';
}

/** Duur van het nieuwe item: de hele bron, of 5 seconden voor een stilstaand beeld. */
export function duurVoorMedia(item: MediaDto, fps: number): number {
  if (item.soort === 'afbeelding' || item.mimeType.startsWith('image/')) {
    return Math.max(1, secondeNaarFrame(5, fps));
  }
  const seconden = item.inspectie?.duurSeconden;
  if (seconden && seconden > 0) return Math.max(1, secondeNaarFrame(seconden, fps));
  return Math.max(1, secondeNaarFrame(item.soort === 'audio' ? 15 : 5, fps));
}

function vrijeStart(project: ProjectModel, type: TrackType, duur: number, voorkeur: number): number {
  const track = tracksVanType(project, type)[0];
  const gewenst = Math.max(0, Math.round(voorkeur));
  if (!track) return gewenst;
  const bezet = track.items.some(
    (i) => gewenst < i.startFrame + i.duurInFrames && i.startFrame < gewenst + duur,
  );
  if (!bezet) return gewenst;
  return volgendVrijFrame(track, duur);
}

export function planPlaatsing(project: ProjectModel, item: MediaDto, voorkeurFrame: number): PlaatsPlan | null {
  if (item.inspectieStatus !== 'klaar') return null;
  const trackType = trackTypeVoorMedia(item);
  if (!trackType || tracksVanType(project, trackType).length === 0) return null;
  const duurInFrames = duurVoorMedia(item, project.fps);
  const beschrijving =
    trackType === 'audio' ? 'Muziek toevoegen' : trackType === 'afbeelding' ? 'Afbeelding toevoegen' : 'Clip toevoegen';
  return {
    itemId: crypto.randomUUID(),
    startFrame: vrijeStart(project, trackType, duurInFrames, voorkeurFrame),
    duurInFrames,
    trackType,
    beschrijving,
  };
}

export function pasPlanToe(project: ProjectModel, item: MediaDto, plan: PlaatsPlan): ProjectModel {
  const track = tracksVanType(project, plan.trackType)[0];
  if (!track) return project;
  const basis = {
    assetId: item.id,
    naam: item.naam,
    startFrame: plan.startFrame,
    duurInFrames: plan.duurInFrames,
  };
  const nieuw =
    plan.trackType === 'video'
      ? { ...maakVideoItem(basis), id: plan.itemId }
      : plan.trackType === 'audio'
        ? { ...maakAudioItem({ ...basis, volume: 0.4 }), id: plan.itemId }
        : { ...maakAfbeeldingItem(basis), id: plan.itemId };
  return voegItemToe(project, track.id, nieuw);
}

/** Zet een bibliotheekbestand op het juiste spoor en selecteert het. */
export function zetMediaOpTijdlijn(
  editor: PlaatsEditor,
  item: MediaDto,
  voorkeurFrame: number,
  opFrame?: (frame: number) => void,
): boolean {
  const project = editor.project;
  if (!project) return false;
  const plan = planPlaatsing(project, item, voorkeurFrame);
  if (!plan) return false;
  editor.bewerk((huidig) => pasPlanToe(huidig, item, plan), plan.beschrijving);
  editor.selecteer(plan.itemId);
  opFrame?.(plan.startFrame);
  return true;
}
